/**
 * AnomalyStrip — Layer 2 of the Subscription Intelligence redesign.
 * Week-over-week signal chips answering: "What changed since last time I looked?"
 */

import React from 'react';
import { Box, Chip, Typography, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DataSourceAttribution from '../../common/DataSourceAttribution';

interface AnomalySignal {
  type: 'positive' | 'warning' | 'alert' | 'info';
  metric: string;
  label: string;
  thisWeek: number | null;
  lastWeek: number | null;
  pctChange: number | null;
  text: string;
}

interface AnomalyStripProps {
  signals: AnomalySignal[];
}

const SIGNAL_CONFIG: Record<AnomalySignal['type'], { color: string; bgColor: string; borderColor: string; Icon: React.ElementType }> = {
  positive: {
    color: '#065F46',
    bgColor: '#ECFDF5',
    borderColor: '#10B981',
    Icon: TrendingUpIcon,
  },
  warning: {
    color: '#92400E',
    bgColor: '#FFFBEB',
    borderColor: '#F59E0B',
    Icon: TrendingDownIcon,
  },
  alert: {
    color: '#7F1D1D',
    bgColor: '#FEF2F2',
    borderColor: '#EF4444',
    Icon: WarningAmberIcon,
  },
  info: {
    color: '#1E3A5F',
    bgColor: '#EFF6FF',
    borderColor: '#3B82F6',
    Icon: InfoOutlinedIcon,
  },
};

const AnomalyStrip: React.FC<AnomalyStripProps> = ({ signals }) => {
  const isEmpty = signals.length === 0;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
        <Typography
          sx={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.8px',
            textTransform: 'uppercase',
            color: '#6B7280',
            whiteSpace: 'nowrap',
          }}
        >
          What Changed — Last 7 Days
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.25 }}>
          {isEmpty ? (
            <Chip
              icon={<CheckCircleOutlineIcon sx={{ fontSize: '14px !important', color: '#6B7280 !important' }} />}
              label="No significant changes this week"
              size="small"
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '11px',
                bgcolor: '#F9FAFB',
                border: '1px solid #E5E7EB',
                color: '#6B7280',
                height: 26,
              }}
            />
          ) : (
            signals.map((signal) => {
              const config = SIGNAL_CONFIG[signal.type];
              const { Icon } = config;
              const pctLabel = signal.pctChange !== null
                ? ` (${signal.pctChange > 0 ? '+' : ''}${signal.pctChange}%)`
                : '';
              const chipLabel = `${signal.label}: ${signal.thisWeek ?? '—'}${pctLabel}`;

              return (
                <Tooltip key={signal.metric} title={signal.text} placement="bottom" arrow>
                  <Chip
                    icon={<Icon sx={{ fontSize: '14px !important', color: `${config.color} !important` }} />}
                    label={chipLabel}
                    size="small"
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '11px',
                      bgcolor: config.bgColor,
                      border: `1px solid ${config.borderColor}`,
                      color: config.color,
                      height: 26,
                      cursor: 'help',
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              );
            })
          )}
        </Box>
      </Box>
      <DataSourceAttribution
        explainer="Week-over-week signal detection. Thresholds: ≥10% change in new subscribers, ≥20% change in cancellations."
        source="adminSubscriptions /intelligence — buildAnomalySignals()"
        endpoint="/api/admin/subscriptions/intelligence"
      />
    </Box>
  );
};

export default AnomalyStrip;
